import { useEffect, useRef, useState, useCallback, type ReactNode } from "react";

export type ContextMenuItem =
  | {
      label: string;
      action?: () => void;
      icon?: string;
      checked?: boolean;
      disabled?: boolean;
      submenu?: ContextMenuItem[];
    }
  | "separator";

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

function MenuItemRow({
  item,
  onClose,
}: {
  item: Exclude<ContextMenuItem, "separator">;
  onClose: () => void;
}) {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [submenuPos, setSubmenuPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const rowRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openSubmenu = useCallback(() => {
    if (!item.submenu || item.disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (rowRef.current) {
      const rect = rowRef.current.getBoundingClientRect();
      let sx = rect.right + 2;
      let sy = rect.top;
      // Flip horizontally if we'd go off screen
      if (sx + 200 > window.innerWidth) {
        sx = rect.left - 202;
      }
      // Clamp vertically
      if (sy + 200 > window.innerHeight) {
        sy = window.innerHeight - 220;
      }
      setSubmenuPos({ x: sx, y: sy });
    }
    setSubmenuOpen(true);
  }, [item.submenu, item.disabled]);

  const closeSubmenu = useCallback(() => {
    timeoutRef.current = setTimeout(() => setSubmenuOpen(false), 150);
  }, []);

  const handleClick = () => {
    if (item.disabled) return;
    if (item.submenu) {
      openSubmenu();
      return;
    }
    item.action?.();
    onClose();
  };

  return (
    <>
      <div
        ref={rowRef}
        className={`wm-ctx__item ${item.disabled ? "wm-ctx__item--disabled" : ""} ${item.submenu ? "wm-ctx__item--has-sub" : ""}`}
        onClick={handleClick}
        onMouseEnter={openSubmenu}
        onMouseLeave={closeSubmenu}
      >
        <span className="wm-ctx__item-icon">
          {item.icon && (
            <svg viewBox="0 0 24 24">
              <path d={item.icon} />
            </svg>
          )}
        </span>
        <span className="wm-ctx__item-label">{item.label}</span>
        {item.checked !== undefined && (
          <span className="wm-ctx__item-check">
            {item.checked && (
              <svg viewBox="0 0 24 24">
                <path d="M5 12l5 5L20 7" />
              </svg>
            )}
          </span>
        )}
        {item.submenu && (
          <span className="wm-ctx__item-arrow">
            <svg viewBox="0 0 24 24">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </span>
        )}
      </div>
      {submenuOpen && item.submenu && (
        <ContextMenuPanel
          x={submenuPos.x}
          y={submenuPos.y}
          items={item.submenu}
          onClose={onClose}
          isSubmenu
        />
      )}
    </>
  );
}

function ContextMenuPanel({
  x,
  y,
  items,
  onClose,
  isSubmenu,
}: ContextMenuProps & { isSubmenu?: boolean }) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });

  // Adjust position to keep on-screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      let nx = x;
      let ny = y;
      if (nx + rect.width > window.innerWidth - 4) {
        nx = window.innerWidth - rect.width - 4;
      }
      if (ny + rect.height > window.innerHeight - 4) {
        ny = window.innerHeight - rect.height - 4;
      }
      if (nx < 0) nx = 4;
      if (ny < 0) ny = 4;
      setPos({ x: nx, y: ny });
    }
  }, [x, y]);

  return (
    <div
      ref={menuRef}
      className={`wm-ctx ${isSubmenu ? "wm-ctx--sub" : ""}`}
      style={{ left: pos.x, top: pos.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        if (item === "separator") {
          return <div key={`sep-${i}`} className="wm-ctx__sep" />;
        }
        return <MenuItemRow key={`${item.label}-${i}`} item={item} onClose={onClose} />;
      })}
    </div>
  );
}

export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  // Close on click outside or Escape
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Check if click is within any context menu
      const target = e.target as HTMLElement;
      if (target.closest(".wm-ctx")) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Use a microtask to avoid closing immediately from the same right-click event
    requestAnimationFrame(() => {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleKey);
    });
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div className="wm-ctx__overlay">
      <ContextMenuPanel x={x} y={y} items={items} onClose={onClose} />
    </div>
  );
}

// Hook for easy context menu state management
export function useContextMenu() {
  const [menu, setMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);

  const show = useCallback((e: React.MouseEvent, items: ContextMenuItem[]) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, items });
  }, []);

  const close = useCallback(() => setMenu(null), []);

  const render = useCallback((): ReactNode => {
    if (!menu) return null;
    return <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={close} />;
  }, [menu, close]);

  return { show, close, render, isOpen: !!menu };
}
